<script lang="ts">
  import { t } from '$lib/i18n/index.svelte';

  // The new worker no longer activates by itself — it installs and waits, so a
  // deploy can never hijack a launch in progress. That makes this toast the ONLY
  // way an update ever lands: it finds the waiting worker, offers the swap, and
  // performs it when the user says so.

  let waiting = $state<ServiceWorker | null>(null);
  /// Set only when WE asked for the swap, so the controllerchange that fires on a
  /// first install (clientsClaim) cannot turn into a reload nobody asked for.
  let swapping = false;

  function watch(registration: ServiceWorkerRegistration): void {
    // A worker that finished installing while the app was closed is already
    // parked in `waiting` by the time we look.
    if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
      waiting = registration.waiting;
    }
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (installing === null) {
        return;
      }
      installing.addEventListener('statechange', () => {
        // `installed` with a controller already present means an update. Without
        // one it is the first install, which is not news worth a toast.
        if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
          waiting = registration.waiting ?? installing;
        }
      });
    });
  }

  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (registration !== undefined) {
          watch(registration);
        }
      })
      .catch(() => undefined);

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (swapping) {
        location.reload();
      }
    });
  }

  function applyUpdate(): void {
    swapping = true;
    waiting?.postMessage({ type: 'SKIP_WAITING' });
    // The reload normally comes from controllerchange above. This is the backstop
    // for a worker that never takes over: a plain reload still picks up the new
    // shell, just one launch later.
    setTimeout(() => location.reload(), 2000);
  }
</script>

{#if waiting !== null}
  <div class="toast" role="status">
    <span>{t('common.updated')}</span>
    <button onclick={applyUpdate}>{t('common.reload')}</button>
  </div>
{/if}

<style>
  .toast {
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    background: var(--ink);
    color: var(--bg);
    border-radius: 999px;
    padding: 10px 16px;
    display: flex;
    gap: 12px;
    align-items: center;
    box-shadow: 0 4px 16px rgb(0 0 0 / 25%);
    z-index: 50;
  }
  .toast button {
    border: 0;
    background: transparent;
    color: inherit;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }
</style>
